import { ChangeEvent, ClipboardEvent, useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useNavigate } from "react-router-dom";
import { endpointInstance } from "../methods/Methods";
import PaymentForms from "../components/PaymentForms";
import { Button, Col, Input, Row, Typography } from "antd";
import { StyleSheet } from "../StyleSheet";
import HeaderComponent from "../components/HeaderComponent";
import vars from "../vars";
import Loading from "../components/Loading";
import { images } from "../routes/ImageRoutes";
import stylesCss from "./landing.module.css";
import InfoCardComponent from "../components/InfoCardComponent";

const { Text } = Typography;

const Landing = () => {
    const navigate = useNavigate();
    const [clientID, setClientID] = useState<string>("");
    const [resultOfCaptcha, setResultOfCaptcha] = useState<string>("");
    const [btnDisable, setBtnDisable] = useState<boolean>(true);
    const [hasError, setHasError] = useState<boolean>(false);
    const [hasErrorRecharges, setHasErrorRecharges] = useState<boolean>(false);
    const [message, setMessage] = useState<string>("");
    const [messageRecharges, setMessageRecharges] = useState<string>("");
    const refOfCaptcha = useRef<ReCAPTCHA>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const call = endpointInstance;
    const [numberPhone, setNumberPhone] = useState<string>("");
    const [btnNumberDisable, setBtnNumberDisable] = useState<boolean>(true);

    const resetCaptchaState = () => {
        setResultOfCaptcha("");
        endpointInstance.setToken("");
        localStorage.removeItem("captchaToken");
        setBtnDisable(true);
        setBtnNumberDisable(true);
        refOfCaptcha.current?.reset();
    };

    useEffect(() => {
        resetCaptchaState();
        setHasError(false);
        setHasErrorRecharges(false);
        setMessage("");
        setMessageRecharges("");
    }, []);

    useEffect(() => {
        if (clientID.length === 10 && resultOfCaptcha) {
            endpointInstance.setToken(resultOfCaptcha);
            setBtnDisable(false);
        } else {
            setBtnDisable(true);
        }
    }, [clientID, resultOfCaptcha]);

    useEffect(() => {
        if (numberPhone.length === 10 && resultOfCaptcha) {
            endpointInstance.setToken(resultOfCaptcha);
            setBtnNumberDisable(false);
        } else {
            setBtnNumberDisable(true);
        }
    }, [numberPhone, resultOfCaptcha]);

    useEffect(() => {
        if (hasError || message) {
            setHasError(false);
            setMessage("");
        }
    }, [clientID]);

    useEffect(() => {
        if (hasErrorRecharges || messageRecharges) {
            setHasErrorRecharges(false);
            setMessageRecharges("");
        }
    }, [numberPhone]);

    const redirectToPayment = () => {
        navigate("/payments");
    };

    const getPaypalClient = async () => {
        const body = {
            sucursal: clientID.substring(0, 3),
            suscriptor: clientID.substring(3, 10),
        };
        await call
            .paypalID(body)
            .then((response) => {
                if (response.data.data[0].mensaje) {
                    localStorage.setItem("clientId", response.data.data[0].mensaje);
                }
            })
            .catch(() => { });
    };

    const validate = async () => {
        const body = {
            sucursal: clientID.substring(0, 3),
            suscriptor: clientID.substring(3, 10),
        };

        setHasError(false);
        setMessage("");
        setLoading(true);

        try {
            const response = await call.getUserActive(body);
            if (
                response.data.data[0].estatus === "true" &&
                response.data.data[0].mensaje === "Con servicio"
            ) {
                localStorage.setItem("idCliente", clientID);
                await getPaypalClient();
                redirectToPayment();
            } else if (response.data.data[0].mensaje === "Sin servicio") {
                setMessage(
                    "Contrato sin servicio. Favor de revisar los datos proporcionados."
                );
                setHasError(true);
            } else if (response.data.data[0].respuesta) {
                setMessage(response.data.data[0].respuesta);
                setHasError(true);
            } else {
                setMessage(
                    "Hubo un error al procesar su solicitud, intente más tarde."
                );
                setHasError(true);
            }
        } catch {
            setMessage(
                "Hubo un error al procesar su solicitud, intente más tarde."
            );
            setHasError(true);
        } finally {
            setLoading(false);
            resetCaptchaState();
        }
    };

    const handleCaptchaChange = (value: string | null) => {
        setResultOfCaptcha(value || "");
        if (value) {
            localStorage.setItem("captchaToken", value);
        } else {
            resetCaptchaState();
        }
    };

    const handleCaptchaExpired = () => {
        resetCaptchaState();
    };

    const handleCopy = (e: ClipboardEvent<HTMLInputElement>) => {
        // Prevent copying when someone tries to copy from the input
        e.preventDefault();
    };

    const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
        // Prevent pasting when someone tries to paste into the input
        e.preventDefault();
    };

    const validateNumber = async () => {
        setHasErrorRecharges(false);
        setMessageRecharges("");
        setLoading(true);
        await call
            .getRecharges(numberPhone)
            .then(async (response) => {
                if (response.data.data[0].recargar === 1) {
                    localStorage.setItem("numero", response.data.data[0].numero);
                    localStorage.setItem("rechargeData", JSON.stringify(response.data.data[0]));
                    navigate("/recharges", { state: response.data.data[0] });
                } else {
                    setMessageRecharges(response.data.data[0].mensaje);
                    setHasErrorRecharges(true);
                }
            })
            .catch(() => {
                setMessageRecharges(
                    "Hubo un error al procesar su solicitud, intente más tarde."
                );
                setHasErrorRecharges(true);
            })
            .finally(() => {
                setLoading(false);
                resetCaptchaState();
            });
    }

    return (
        <>
            {
                loading ?
                    <Loading />
                    :
                    <>
                        <HeaderComponent />
                        <Row style={styles.titleRow}>
                            <Text style={styles.textTitle}>
                                Paga en Línea tus servicios de Mega
                            </Text>
                        </Row>
                        <Row style={styles.formContainer}>
                            <Col style={styles.divContainer}>
                                <Text style={styles.subtitle}>
                                    Ingresa tu contrato a 10 dígitos
                                </Text>
                                <Input
                                    required
                                    type="text"
                                    value={clientID}
                                    placeholder="Ingresa tu contrato a 10 dígitos"
                                    style={styles.input}
                                    maxLength={10}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                        setClientID(e.target.value.replace(/[^0-9]/g, ""))
                                    }
                                    onCopy={handleCopy}
                                    onCut={handleCopy}
                                    onPaste={handlePaste}
                                />
                                <Button
                                    size='large'
                                    style={btnDisable ? styles.buttonDisabled : styles.buttonPrimary}
                                    disabled={btnDisable}
                                    onClick={() => validate()}
                                >
                                    INGRESAR
                                </Button>
                                <ReCAPTCHA
                                    ref={refOfCaptcha}
                                    sitekey={vars.sitekey}
                                    onChange={handleCaptchaChange}
                                    onExpired={handleCaptchaExpired}
                                    theme="light"
                                    style={{ marginTop: 30, marginBottom: 20 }}
                                />
                                <Text style={{ fontSize: 12, color: '#000', textAlign: 'center', padding: 0 }}>Validación de Seguridad para continuar con cualquier operación</Text>
                                {
                                    (hasError || hasErrorRecharges) && (message || messageRecharges) ?
                                        <InfoCardComponent
                                            color="#FFC439" colorText="#000"
                                            description={message || messageRecharges}
                                        />
                                        :
                                        null
                                }

                            </Col>
                        </Row>
                        <Row style={styles.formContainer} justify="center">
                            <Col style={styles.divContainer}>
                                <Text style={styles.subtitle}>
                                    Realiza recargas de saldo en Mega Móvil
                                </Text>
                                <Input
                                    type="text"
                                    placeholder="Ingresa tu número celular a 10 dígitos"
                                    value={numberPhone}
                                    style={styles.input}
                                    maxLength={10}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                        setNumberPhone(e.target.value.replace(/[^0-9]/g, ""))
                                    }
                                />
                                <Button
                                    size='large'
                                    style={btnNumberDisable ? styles.buttonDisabled : styles.buttonPrimary}
                                    onClick={() => validateNumber()}
                                    disabled={btnNumberDisable}
                                >
                                    RECARGAR
                                </Button>
                            </Col>
                        </Row>
                        <Row style={{ ...styles.formContainer, marginTop: "1rem" }} justify="center">
                            <img
                                src={images.cards}
                                alt="Tarjetas"
                                className={stylesCss.cards}
                            />
                        </Row>
                        <PaymentForms />
                    </>
            }
        </>
    );
};

const styles = StyleSheet.create({
    titleRow: {
        marginTop: 10,
        display: 'flex',
        justifyContent: 'center',
    },
    textTitle: {
        fontSize: 40,
        color: 'black',
        fontWeight: 700,
        textAlign: 'center',
        lineHeight: 1.2
    },
    subtitle: {
        fontSize: 22,
        color: 'black',
        textAlign: 'center'
    },
    input: {
        width: 380,
        height: 40,
        backgroundColor: "#f0efff",
        textAlign: 'center',
        borderColor: 'transparent',
        marginTop: "1.5rem"
    },
    divContainer: {
        flexDirection: "column",
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    buttonPrimary: {
        backgroundColor: '#007aff',
        borderRadius: 30,
        color: 'white',
        border: "none",
        width: 320,
        marginTop: 25,
    },
    buttonDisabled: {
        backgroundColor: '#787B7F',
        borderRadius: 30,
        color: 'white',
        border: "none",
        width: 320,
        marginTop: 25,
    },
    formContainer: {
        marginTop: "2rem",
        justifyContent: "center"
    },
    lineDivider: {
        borderColor: 'black',
        minWidth: "100%",
        width: "100%"
    }
});

export default Landing;
